import { Box, useTheme } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { memo, useCallback, useContext, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppContext } from '../../../../App'
import { ChartViewKey } from '../../../../assets/constants'
import { ExtraSmallButton, NonInteractiveTooltip } from '../../../custom'
import LiveText from '../../../common/LiveText'
import { updateMultiStrikeChartConfig } from '../../../../reducer/chart.reducer'
import {
    CallPutType,
    MultiStrikeChartTypes,
    setMultiStrikeNearAtm,
    setMultiStrikeSecurities,
} from '../../../../reducer/multiStrikeChart.reducer'
import { selectMultiStrikeChartConfigSecurities, selectMultiStrikeStatusLineSettings } from '../../../../selectors/chart'
import { selectMultiStrikeChartSecurities } from '../../../../selectors/multiStrikeChart'
import store from '../../../../store'
import {
    buildMultiStrikeMetadataSubscribe,
    getMultiStrikeValueSelector,
} from './multiStrikeMetadataLiveDisplay'
import {
    getMultiStrikeMetadataTableRowSx,
    MULTI_STRIKE_METADATA_ACTIONS_CLASS,
    MULTI_STRIKE_METADATA_ACTIONS_SX,
    MULTI_STRIKE_METADATA_ROW_CLASS,
    MULTI_STRIKE_METADATA_STRIKE_CELL_SX,
    MULTI_STRIKE_METADATA_VALUE_CELL_SX,
} from './multiStrikeMetadataLayout'
import { pcrSnapshotKey } from '../../combinedLegSeries'

type Props = {
    viewKey: string
    strike: string
    seriesColor?: string
    callPut?: CallPutType[]
    chartType: MultiStrikeChartTypes
}

const ALL_LEGS: CallPutType[] = ['CE', 'PE']

const MultiStrikePcrMetadataRow = ({ viewKey, strike, seriesColor, callPut, chartType }: Props) => {
    const theme = useTheme()
    const dispatch = useDispatch()
    const appContext = useContext(AppContext)
    const isWidget = useMemo(() => viewKey !== ChartViewKey.MULTI_STRIKES_CHART, [viewKey])
    const statusLineSettings = useSelector(selectMultiStrikeStatusLineSettings(viewKey))

    const seriesKey = pcrSnapshotKey(strike)
    const isVisible = (callPut ?? ALL_LEGS).length > 0

    const subscribe = useMemo(() => buildMultiStrikeMetadataSubscribe(viewKey, [seriesKey]), [viewKey, seriesKey])
    const selectText = useMemo(
        () => getMultiStrikeValueSelector(viewKey, seriesKey, chartType),
        [viewKey, seriesKey, chartType]
    )

    const readSecurities = useCallback(() => {
        const state = store.getState()
        return isWidget
            ? selectMultiStrikeChartConfigSecurities(viewKey)(state)
            : selectMultiStrikeChartSecurities(state)
    }, [isWidget, viewKey])

    const toggleVisible = useCallback(() => {
        const updated = { ...readSecurities() }
        updated[strike] = isVisible ? [] : ALL_LEGS
        if (isWidget) {
            dispatch(updateMultiStrikeChartConfig({ viewKey, config: { securities: updated } }))
        } else {
            dispatch(setMultiStrikeSecurities(updated))
        }
        const chart = appContext.getMultiStrikeChart(viewKey)
        if (chart) {
            chart.secondarySecurities = {
                ...chart.secondarySecurities,
                [strike]: { ...chart.secondarySecurities[strike], callPut: updated[strike] },
            }
            chart.pcrSeries.applyVisibility()
        }
    }, [appContext, dispatch, isVisible, isWidget, readSecurities, strike, viewKey])

    const removeStrike = useCallback(() => {
        const chart = appContext.getMultiStrikeChart(viewKey)
        if (!chart) return
        const updated = { ...readSecurities() }
        chart.dataManager.removeStrike(strike)
        delete updated[strike]
        if (isWidget) {
            dispatch(
                updateMultiStrikeChartConfig({
                    viewKey,
                    config: { securities: updated, nearAtm: undefined, atm: undefined },
                })
            )
        } else {
            dispatch(setMultiStrikeSecurities(updated))
            dispatch(setMultiStrikeNearAtm({ nearAtm: undefined, atm: undefined }))
        }
    }, [appContext, dispatch, isWidget, readSecurities, strike, viewKey])

    if (!((statusLineSettings.indicatorTitle ?? true) && (statusLineSettings.indicatorValues ?? true))) {
        return null
    }

    return (
        <Box
            component="tr"
            className={MULTI_STRIKE_METADATA_ROW_CLASS}
            sx={{ color: 'text.secondary', ...getMultiStrikeMetadataTableRowSx(theme) }}
        >
            <Box
                component="td"
                sx={{
                    ...MULTI_STRIKE_METADATA_STRIKE_CELL_SX,
                    cursor: 'pointer',
                    textDecoration: isVisible ? 'none' : 'line-through',
                }}
            >
                <NonInteractiveTooltip placement="top" title={isVisible ? 'hide' : 'show'} arrow>
                    <Box component="span" onClick={toggleVisible}>
                        {strike}
                    </Box>
                </NonInteractiveTooltip>
            </Box>
            <Box
                component="td"
                sx={{
                    ...MULTI_STRIKE_METADATA_VALUE_CELL_SX,
                    color: seriesColor ?? theme.palette.text.primary,
                    opacity: isVisible ? 1 : 0.3,
                    paddingRight: '20px',
                }}
            >
                {isVisible ? <LiveText subscribe={subscribe} selectText={selectText} /> : '-'}
                <Box
                    className={MULTI_STRIKE_METADATA_ACTIONS_CLASS}
                    sx={{ ...MULTI_STRIKE_METADATA_ACTIONS_SX, color: 'text.secondary' }}
                >
                    <NonInteractiveTooltip placement="top" title="Remove" arrow>
                        <ExtraSmallButton
                            color="inherit"
                            size="small"
                            onClick={removeStrike}
                            data-event={'Multi strike chat remove series clicked'}
                            data-event-series-name={seriesKey}
                            sx={{ height: '18px', width: '18px', minWidth: '18px', p: 0, color: 'inherit' }}
                        >
                            <CloseIcon sx={{ fontSize: '16px' }} />
                        </ExtraSmallButton>
                    </NonInteractiveTooltip>
                </Box>
            </Box>
        </Box>
    )
}

export default memo(MultiStrikePcrMetadataRow)
